import { useCallback } from "react";

import dayjs from "dayjs";
import { useNavigate } from "react-router";

import { BbdDateCorrectionOrigin } from "analytics/events";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";

import { useBbdCorrectionServices } from "./useBbdCorrectionServices";
import { useBbdCorrectionStore } from "./useBbdCorrectionStore";

const PRODUCT_BBD_UPDATE_SUCCESS_TOAST_ID = "product_bbd_update_success_toast_id";

const getState = () => useBbdCorrectionStore.getState();

export function useBbdCorrectionActions() {
  const { showToastUI } = useCustomToast();
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "bbd_correction_error",
  });
  const { fetchProducts, fetchProductsByText, fetchProductWithBBDTask, updateProductBbd } =
    useBbdCorrectionServices();

  // --- Toast actions ---

  const showErrorToast = useCallback(() => {
    showToastAndPlayError({
      title: "flows.inventory.bbd-corrections-machines.actions.error-toast.title",
    });
  }, [showToastAndPlayError]);

  const showSuccessToast = useCallback(() => {
    showToastUI({
      id: PRODUCT_BBD_UPDATE_SUCCESS_TOAST_ID,
      status: "success",
      title:
        "flows.inventory.bbd-corrections-machines.actions.product-bbd-update-success-toast-id.title",
    });
  }, [showToastUI]);

  // --- Analytics ---

  const sendStartEvent = useCallback(() => {
    const state = getState();
    const origin: BbdDateCorrectionOrigin = state.isFromSearch ? "search_bar" : "scanned_product";
    sendSegmentTrackEvent("bbdDateCorrectionStarted", {
      origin,
      product_sku: state.selectedInventoryProductSku as string,
      shelf_number: state.selectedInventoryProductShelf ?? "none",
    });
  }, [sendSegmentTrackEvent]);

  const sendFinishEvent = useCallback(
    (closestBbd: Date, initialClosestBbd: Date | null) => {
      const state = getState();
      const nextExpirationDateAfter = dayjs(closestBbd).format("YYYY-MM-DD");
      const nextExpirationDateBefore = initialClosestBbd
        ? dayjs(initialClosestBbd).format("YYYY-MM-DD")
        : "none";
      sendSegmentTrackEvent("bbdDateCorrectionFinished", {
        product_sku: state.selectedInventoryProductSku as string,
        next_expiration_date_after: nextExpirationDateAfter,
        next_expiration_date_before: nextExpirationDateBefore,
      });
    },
    [sendSegmentTrackEvent],
  );

  // --- Async flows ---

  const validateAndEnterDeclare = useCallback(async () => {
    const state = getState();
    const sku = state.selectedInventoryProductSku;
    if (!sku) return;

    getState().setPhase("fetchingBbdTask");
    try {
      await fetchProductWithBBDTask(sku);
      getState().setPhase("declareBbdCorrection");
      sendStartEvent();
    } catch (error: any) {
      const errorMessage = error?.toString() ?? "";
      if (errorMessage.includes("productIsNotBbd")) {
        getState().setPhase("notBbdProduct");
      } else if (errorMessage.includes("hasOpenTask")) {
        getState().setPhase("hasOpenTask");
      } else {
        showErrorToast();
        getState().enterIdle();
        navigate(routes.inventory.bbdCorrections.root, { replace: true });
      }
    }
  }, [fetchProductWithBBDTask, navigate, sendStartEvent, showErrorToast]);

  const scan = useCallback(
    async (barcode: string) => {
      getState().setPhase("resolvingProducts");
      try {
        const products = await fetchProducts(barcode);

        if (products.length === 0) {
          getState().enterIdle();
          return;
        }

        getState().setInventoryProductsList(products);

        if (products.length > 1) {
          getState().setPhase("selectProduct");
          navigate(routes.inventory.bbdCorrections.selectProduct);
        } else {
          getState().setFirstInventoryProductAsSelected();
          getState().setPhase("fetchingBbdTask");
          navigate(routes.inventory.bbdCorrections.declare, { replace: true });
          await validateAndEnterDeclare();
        }
      } catch {
        getState().enterIdle();
      }
    },
    [fetchProducts, navigate, validateAndEnterDeclare],
  );

  const submitBbdUpdate = useCallback(
    async (closestBbd: Date, initialClosestBbd: Date | null) => {
      const state = getState();
      const sku = state.selectedInventoryProductSku;
      if (!sku) return;

      getState().setPhase("declareBbdCorrectionLoading");
      try {
        await updateProductBbd(sku, closestBbd);
        sendFinishEvent(closestBbd, initialClosestBbd);
        getState().setPhase("declareBbdCorrectionDone");
      } catch {
        showErrorToast();
        getState().setPhase("declareBbdCorrection");
      }
    },
    [sendFinishEvent, showErrorToast, updateProductBbd],
  );

  const terminate = useCallback(() => {
    showSuccessToast();
    getState().enterIdle();
    navigate(routes.inventory.bbdCorrections.root, { replace: true });
  }, [navigate, showSuccessToast]);

  const searchProducts = useCallback(
    async (query: string) => {
      getState().resetInventoryProductsList();
      getState().setPhase("searchLoading");
      try {
        const products = await fetchProductsByText(query);
        getState().setInventoryProductsList(products);
        getState().setPhase("search");
      } catch {
        getState().setPhase("search");
      }
    },
    [fetchProductsByText],
  );

  const activateSearch = useCallback(() => {
    getState().resetInventoryProductsList();
    getState().setIsFromSearch(true);
    getState().setPhase("search");
  }, []);

  const cancelSearch = useCallback(() => {
    getState().resetInventoryProductsList();
    getState().enterIdle();
  }, []);

  const resetSearchResults = useCallback(() => {
    getState().resetInventoryProductsList();
  }, []);

  const selectProduct = useCallback(
    async (sku: string, shelf: string | null) => {
      getState().setInventoryProduct(sku, shelf);
      getState().setPhase("fetchingBbdTask");
      navigate(routes.inventory.bbdCorrections.declare, { replace: true });
      await validateAndEnterDeclare();
    },
    [navigate, validateAndEnterDeclare],
  );

  const clearListAndGoIdle = useCallback(() => {
    getState().reset();
    navigate(routes.inventory.bbdCorrections.root, { replace: true });
  }, [navigate]);

  const goBackToIdle = useCallback(() => {
    getState().enterIdle();
    navigate(routes.inventory.bbdCorrections.root, { replace: true });
  }, [navigate]);

  return {
    scan,
    searchProducts,
    submitBbdUpdate,
    terminate,
    activateSearch,
    cancelSearch,
    resetSearchResults,
    selectProduct,
    clearListAndGoIdle,
    goBackToIdle,
  };
}
